import React from 'react'
import Grid from '@mui/material/Grid'
import Tooltip from '@mui/material/Tooltip'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import GroupIcon from '@mui/icons-material/Group'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import CancelIcon from '@mui/icons-material/Cancel'
import StarIcon from '@mui/icons-material/Star'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { currency } from '@/v2/utils/format'
import {
  MetricCard,
  MetricContent,
  MetricHeader,
  MetricIcon,
  MetricLabel,
  MetricValue,
  MetricFooter,
  MetricChange,
} from '../styles'

const MetricCards = ({ metrics, conversionRate }) => {
  const showRate =
    metrics?.summary?.show_rate ??
    (metrics?.summary?.total_bookings > 0
      ? (
          (metrics.summary.total_showed_up / metrics.summary.total_bookings) *
          100
        ).toFixed(1)
      : 0)

  const totalRevenue = metrics?.summary?.total_revenue || 0

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={2}>
        <Tooltip
          title="Total number of appointments scheduled in the selected period"
          placement="top"
        >
          <MetricCard elevation={0} $color="link">
            <MetricContent>
              <MetricHeader>
                <MetricIcon $color="link">
                  <GroupIcon />
                </MetricIcon>
                <MetricLabel>Total Bookings</MetricLabel>
              </MetricHeader>
              <MetricValue>{metrics?.summary?.total_bookings || 0}</MetricValue>
            </MetricContent>
            <MetricFooter>
              <MetricChange $positive={true}>
                All scheduled appointments
              </MetricChange>
            </MetricFooter>
          </MetricCard>
        </Tooltip>
      </Grid>

      <Grid item xs={12} sm={6} md={2}>
        <Tooltip
          title="Patients who attended their scheduled appointments"
          placement="top"
        >
          <MetricCard elevation={0} $color="success">
            <MetricContent>
              <MetricHeader>
                <MetricIcon $color="success">
                  <EventAvailableIcon />
                </MetricIcon>
                <MetricLabel>Showed Up</MetricLabel>
              </MetricHeader>
              <MetricValue>
                {metrics?.summary?.total_showed_up || 0}
              </MetricValue>
            </MetricContent>
            <MetricFooter>
              <MetricChange $positive={true}>
                Attended appointments
              </MetricChange>
            </MetricFooter>
          </MetricCard>
        </Tooltip>
      </Grid>

      <Grid item xs={12} sm={6} md={2}>
        <Tooltip
          title="Patients who missed their scheduled appointments without notice"
          placement="top"
        >
          <MetricCard elevation={0} $color="error">
            <MetricContent>
              <MetricHeader>
                <MetricIcon $color="error">
                  <CancelIcon />
                </MetricIcon>
                <MetricLabel>No-Shows</MetricLabel>
              </MetricHeader>
              <MetricValue>{metrics?.summary?.total_no_shows || 0}</MetricValue>
            </MetricContent>
            <MetricFooter>
              <MetricChange $positive={false}>Missed appointments</MetricChange>
            </MetricFooter>
          </MetricCard>
        </Tooltip>
      </Grid>

      <Grid item xs={12} sm={6} md={2}>
        <Tooltip
          title="Percentage of patients who showed up for their appointments"
          placement="top"
        >
          <MetricCard elevation={0} $color="success">
            <MetricContent>
              <MetricHeader>
                <MetricIcon $color="success">
                  <TrendingUpIcon />
                </MetricIcon>
                <MetricLabel>Show Rate</MetricLabel>
              </MetricHeader>
              <MetricValue>{showRate}%</MetricValue>
            </MetricContent>
            <MetricFooter>
              <MetricChange $positive={showRate >= 70}>
                Showed Up ÷ Bookings
              </MetricChange>
            </MetricFooter>
          </MetricCard>
        </Tooltip>
      </Grid>

      <Grid item xs={12} sm={6} md={2}>
        <Tooltip
          title="Patients who started a treatment plan (conversion from appointment to paying customer)"
          placement="top"
        >
          <MetricCard elevation={0} $color="warning">
            <MetricContent>
              <MetricHeader>
                <MetricIcon $color="warning">
                  <StarIcon />
                </MetricIcon>
                <MetricLabel>Total Starts</MetricLabel>
              </MetricHeader>
              <MetricValue>{metrics?.summary?.total_starts || 0}</MetricValue>
            </MetricContent>
            <MetricFooter>
              <MetricChange $positive={conversionRate > 30}>
                <TrendingUpIcon fontSize="small" />
                {conversionRate}% conversion rate
              </MetricChange>
            </MetricFooter>
          </MetricCard>
        </Tooltip>
      </Grid>

      <Grid item xs={12} sm={6} md={2}>
        <Tooltip
          title="Total revenue from treatment plans started in the selected period"
          placement="top"
        >
          <MetricCard elevation={0} $color="success">
            <MetricContent>
              <MetricHeader>
                <MetricIcon $color="success">
                  <AttachMoneyIcon />
                </MetricIcon>
                <MetricLabel>Total Revenue</MetricLabel>
              </MetricHeader>
              <MetricValue>{currency(totalRevenue)}</MetricValue>
            </MetricContent>
            <MetricFooter>
              <MetricChange $positive={totalRevenue > 0}>
                From treatment plan starts
              </MetricChange>
            </MetricFooter>
          </MetricCard>
        </Tooltip>
      </Grid>
    </Grid>
  )
}

export default MetricCards
